import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import HoverTooltip from '../../../components/ui/HoverTooltip';

const PropertyCard = ({ 
  property, 
  variant = 'card', 
  onSave, 
  isHighlighted = false,
  onBidClick,
  onContactClick,
  canBid = true,
  isOwnProduct = false,
  ownProductBidTooltip = '',
  ownProductContactTooltip = '',
}) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const isOfferOpen = property?.openToOffer !== false;
  const canBidOnProperty = canBid && isOfferOpen && !isOwnProduct;
  const canContactSeller = !isOwnProduct;
  const availableQuantity = Number(property?.availableQuantity ?? property?.quantity ?? 0);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    })?.format(price);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US')?.format(num);
  };

  const handleSave = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (onSave) {
      onSave(property?.id, !property?.isSaved);
    }
  };

  const handleBidClick = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (!canBidOnProperty) {
      return;
    }
    if (onBidClick) {
      onBidClick(property);
    }
  };

  const handleImageNavigation = (direction, e) => {
    e?.preventDefault();
    e?.stopPropagation();
    
    if (direction === 'next') {
      setCurrentImageIndex((prev) => 
        prev === property?.images?.length - 1 ? 0 : prev + 1
      );
    } else {
      setCurrentImageIndex((prev) => 
        prev === 0 ? property?.images?.length - 1 : prev - 1
      );
    }
  };

  const handleContactSeller = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (!canContactSeller) {
      return;
    }
    if (typeof onContactClick === 'function') {
      onContactClick(property);
      return;
    }

    if (property?.seller?.phone) {
      window.open(`tel:${property.seller.phone}`, '_self');
    }
  };

  const getHighestBid = () => {
    if (!property?.currentBids?.length) return null;
    return Math.max(...property?.currentBids?.map(bid => bid?.amount));
  };

  const highestBid = getHighestBid();

  if (variant === 'list') {
    return (
      <Link
        to={`/property-details?id=${property?.id}`}
        className={`block card hover:shadow-elevation-2 transition-all duration-200 ease-out
                   ${isHighlighted ? 'ring-2 ring-primary shadow-elevation-2' : ''}`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="p-4">
          <div className="flex space-x-4">
            {/* Product Images */}
            <div className="relative w-48 h-32 flex-shrink-0 overflow-hidden rounded-md">
              <Image
                src={property?.images?.[currentImageIndex]}
                alt={property?.title}
                className="w-full h-full object-cover"
              />
              
              {/* Organic Badge */}
              {property?.organic && (
                <div className="absolute top-2 left-2 bg-success text-white px-2 py-1 rounded-md text-xs font-medium">
                  Organic
                </div>
              )}

              {/* Image Navigation */}
              {property?.images?.length > 1 && isHovered && (
                <>
                  <button
                    onClick={(e) => handleImageNavigation('prev', e)}
                    className="absolute left-2 top-1/2 transform -translate-y-1/2 w-6 h-6 
                             bg-surface/90 rounded-full flex items-center justify-center
                             hover:bg-surface transition-all duration-200"
                  >
                    <Icon name="ChevronLeft" size={14} />
                  </button>
                  <button
                    onClick={(e) => handleImageNavigation('next', e)}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 w-6 h-6 
                             bg-surface/90 rounded-full flex items-center justify-center
                             hover:bg-surface transition-all duration-200"
                  >
                    <Icon name="ChevronRight" size={14} />
                  </button>
                </>
              )}

              {/* Image Indicators */}
              {property?.images?.length > 1 && (
                <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 
                              flex space-x-1">
                  {property?.images?.map((_, index) => (
                    <div
                      key={index}
                      className={`w-1.5 h-1.5 rounded-full transition-all duration-200 ${
                        index === currentImageIndex ? 'bg-white' : 'bg-white/50'
                      }`}
                    />
                  ))}
                </div>
              )}

              {/* Save Button */}
              <button
                onClick={handleSave}
                className={`absolute top-2 right-2 w-8 h-8 rounded-full flex items-center justify-center
                           transition-all duration-200 ease-out ${
                  property?.isSaved
                    ? 'bg-error text-white' :'bg-surface/90 text-text-secondary hover:bg-surface hover:text-error'
                }`}
              >
                <Icon 
                  name="Heart" 
                  size={16} 
                  fill={property?.isSaved ? "currentColor" : "none"} 
                />
              </button>
            </div>

            {/* Product Details */}
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <h3 className="text-lg font-semibold text-text-primary truncate">
                    {property?.title}
                  </h3>
                  <p className="text-2xl font-bold text-primary">
                    {formatPrice(property?.price)} {property?.priceUnit}
                  </p>
                  {highestBid && (
                    <p className="text-sm text-success">
                      Highest Bid: {formatPrice(highestBid)}
                    </p>
                  )}
                </div>
                
                {property?.daysListed <= 3 && (
                  <span className="bg-success-100 text-success px-2 py-1 rounded-md text-xs font-medium">
                    New
                  </span>
                )}
              </div>

              <p className="text-text-secondary text-sm mb-3 truncate">
                {property?.address}
              </p>

              {/* Product Features */}
              <div className="flex items-center space-x-4 mb-3 text-sm text-text-secondary">
                <div className="flex items-center space-x-1">
                  <Icon name="Package" size={16} />
                  <span>{formatNumber(availableQuantity)} / {formatNumber(property?.quantity)} {property?.unit}</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Icon name="Tag" size={16} />
                  <span className="capitalize">{property?.category}</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Icon name="Calendar" size={16} />
                  <span>{property?.daysListed}d listed</span>
                </div>
              </div>

              {/* Features Tags */}
              <div className="flex flex-wrap gap-1 mb-3">
                {property?.features?.slice(0, 3)?.map((feature, index) => (
                  <span
                    key={index}
                    className="px-2 py-1 bg-primary-100 text-primary text-xs rounded-md"
                  >
                    {feature}
                  </span>
                ))}
                {property?.features?.length > 3 && (
                  <span className="px-2 py-1 bg-secondary-100 text-text-secondary text-xs rounded-md">
                    +{property?.features?.length - 3} more
                  </span>
                )}
              </div>

              {/* Seller Info & Actions */}
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Image
                    src={property?.seller?.avatar}
                    alt={property?.seller?.name}
                    className="w-5 h-5 rounded-full object-cover"
                  />
                  <span className="text-xs text-text-secondary truncate max-w-24">
                    {property?.seller?.name}
                  </span>
                  <div className="flex items-center space-x-1">
                    <Icon name="Star" size={10} className="text-warning fill-current" />
                    <span className="text-xs text-text-secondary">{property?.seller?.rating}</span>
                  </div>
                </div>

                {isHovered && (
                  <div className="flex items-center space-x-2">
                    <HoverTooltip
                      content={isOwnProduct ? ownProductBidTooltip : undefined}
                      className="inline-flex"
                    >
                      <button
                        onClick={handleBidClick}
                        disabled={!canBidOnProperty}
                        className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200 ease-out ${
                          canBidOnProperty
                            ? 'bg-primary text-white hover:bg-primary-700'
                            : 'bg-secondary-200 text-text-secondary cursor-not-allowed'
                        }`}
                      >
                        <Icon name="Gavel" size={14} className="inline mr-1" />
                        {!isOfferOpen ? 'Kapalı' : canBid ? 'Teklif Ver' : 'Giriş'}
                      </button>
                    </HoverTooltip>
                    <HoverTooltip
                      content={isOwnProduct ? ownProductContactTooltip : undefined}
                      className="inline-flex"
                    >
                      <button
                        onClick={handleContactSeller}
                        disabled={!canContactSeller}
                        className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200 ease-out ${
                          canContactSeller
                            ? 'bg-accent-100 text-accent-600 hover:bg-accent-500 hover:text-white'
                            : 'bg-secondary-200 text-text-secondary cursor-not-allowed'
                        }`}
                      >
                        Contact
                      </button>
                    </HoverTooltip>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </Link>
    );
  }

  // Card variant for mobile/grid view
  return (
    <Link
      to={`/property-details?id=${property?.id}`}
      className="block card hover:shadow-elevation-2 transition-all duration-200 ease-out micro-interaction"
    >
      {/* Product Images */}
      <div className="relative h-48 overflow-hidden rounded-t-lg">
        <Image
          src={property?.images?.[currentImageIndex]}
          alt={property?.title}
          className="w-full h-full object-cover"
        />
        
        {/* Organic Badge */}
        {property?.organic && (
          <div className="absolute top-3 left-3 bg-success text-white px-2 py-1 rounded-md text-xs font-medium">
            Organic
          </div>
        )}

        {/* Image Navigation */}
        {property?.images?.length > 1 && (
          <>
            <button
              onClick={(e) => handleImageNavigation('prev', e)}
              className="absolute left-2 top-1/2 transform -translate-y-1/2 w-8 h-8 
                       bg-surface/90 rounded-full flex items-center justify-center
                       hover:bg-surface transition-all duration-200"
            >
              <Icon name="ChevronLeft" size={16} />
            </button>
            <button
              onClick={(e) => handleImageNavigation('next', e)}
              className="absolute right-2 top-1/2 transform -translate-y-1/2 w-8 h-8 
                       bg-surface/90 rounded-full flex items-center justify-center
                       hover:bg-surface transition-all duration-200"
            >
              <Icon name="ChevronRight" size={16} />
            </button>
          </>
        )}

        {/* Image Indicators */}
        {property?.images?.length > 1 && (
          <div className="absolute bottom-3 left-1/2 transform -translate-x-1/2 
                        flex space-x-1">
            {property?.images?.map((_, index) => (
              <div
                key={index}
                className={`w-2 h-2 rounded-full transition-all duration-200 ${
                  index === currentImageIndex ? 'bg-white' : 'bg-white/50'
                }`}
              />
            ))}
          </div>
        )}

        {/* Save Button */}
        <button
          onClick={handleSave}
          className={`absolute top-3 right-3 w-10 h-10 rounded-full flex items-center justify-center
                     transition-all duration-200 ease-out ${
            property?.isSaved
              ? 'bg-error text-white' :'bg-surface/90 text-text-secondary hover:bg-surface hover:text-error'
          }`}
        >
          <Icon 
            name="Heart" 
            size={18} 
            fill={property?.isSaved ? "currentColor" : "none"} 
          />
        </button>

        {/* New Badge */}
        {property?.daysListed <= 3 && (
          <div className="absolute top-3 left-3 bg-success text-white px-2 py-1 rounded-md text-xs font-medium">
            New
          </div>
        )}
      </div>
      {/* Product Details */}
      <div className="p-4">
        <div className="flex items-start justify-between mb-2">
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold text-text-primary truncate">
              {property?.title}
            </h3>
            <p className="text-xl font-bold text-primary">
              {formatPrice(property?.price)} {property?.priceUnit}
            </p>
            {highestBid && (
              <p className="text-sm text-success">
                Highest Bid: {formatPrice(highestBid)}
              </p>
            )}
          </div>
        </div>

        <p className="text-text-secondary text-sm mb-3 truncate">
          {property?.address}
        </p>

        {/* Product Features */}
        <div className="flex items-center space-x-4 mb-4 text-sm text-text-secondary">
          <div className="flex items-center space-x-1">
            <Icon name="Package" size={16} />
            <span>{formatNumber(availableQuantity)} / {formatNumber(property?.quantity)} {property?.unit}</span>
          </div>
          <div className="flex items-center space-x-1">
            <Icon name="Tag" size={16} />
            <span className="capitalize">{property?.category}</span>
          </div>
        </div>

        {/* Features Tags */}
        <div className="flex flex-wrap gap-1 mb-4">
          {property?.features?.slice(0, 2)?.map((feature, index) => (
            <span
              key={index}
              className="px-2 py-1 bg-primary-100 text-primary text-xs rounded-md"
            >
              {feature}
            </span>
          ))}
          {property?.features?.length > 2 && (
            <span className="px-2 py-1 bg-secondary-100 text-text-secondary text-xs rounded-md">
              +{property?.features?.length - 2} more
            </span>
          )}
        </div>

        {/* Seller Info & Actions */}
        <div className="flex items-center justify-between pt-3 border-t border-border">
          <div className="flex items-center space-x-2">
            <Image
              src={property?.seller?.avatar}
              alt={property?.seller?.name}
              className="w-5 h-5 rounded-full object-cover"
            />
            <span className="text-xs text-text-secondary truncate max-w-20">
              {property?.seller?.name}
            </span>
            <div className="flex items-center space-x-1">
              <Icon name="Star" size={8} className="text-warning fill-current" />
              <span className="text-xs text-text-secondary">{property?.seller?.rating}</span>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <HoverTooltip
              content={isOwnProduct ? ownProductBidTooltip : (!isOfferOpen ? 'Bu ürün teklife kapalı.' : undefined)}
              className="inline-flex"
            >
              <button
                onClick={handleBidClick}
                disabled={!canBidOnProperty}
                className={`p-2 rounded-md transition-all duration-200 ease-out ${
                  canBidOnProperty
                    ? 'bg-primary text-white hover:bg-primary-700'
                    : 'bg-secondary-200 text-text-secondary cursor-not-allowed'
                }`}
              >
                <Icon name="Gavel" size={16} />
              </button>
            </HoverTooltip>
            <HoverTooltip
              content={isOwnProduct ? ownProductContactTooltip : undefined}
              className="inline-flex"
            >
              <button
                onClick={handleContactSeller}
                disabled={!canContactSeller}
                className={`p-2 rounded-md transition-all duration-200 ease-out ${
                  canContactSeller
                    ? 'bg-accent-100 text-accent-600 hover:bg-accent-500 hover:text-white'
                    : 'bg-secondary-200 text-text-secondary cursor-not-allowed'
                }`}
              >
                <Icon name="Phone" size={16} />
              </button>
            </HoverTooltip>
          </div>
        </div>
      </div>
    </Link>
  );
};

export default PropertyCard;
